import { useFormikContext } from 'formik';
import { ReactElement } from 'react';
import { object, reach } from 'yup';
import { NeglectedOrDelinquentInformationFormValues } from '../../../types/NeglectedOrDelinquentInformationModel';
import { PERIOD_COUNT_FIELDS } from '../../../types/NeglectedOrDelinquentPeriodCountFields';
import { PanelType } from '../../../types/PanelType';
import { createNeglectedOrDelinquentInformationValidationSchema } from '../../../types/Validation/NeglectedOrDelinquentInformationValidationSchema';
import { isValidNeglectedOrDelinquentCount } from '../../../utilities/neglectedOrDelinquentUtilities';
import FormInputLabel from '../../FormInputs/FormInputLabel/FormInputLabel';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';

const NeglectedOrDelinquentEmploymentPanel = ({
    disabled,
    validationSchema,
}: {
    disabled: boolean;
    validationSchema: ReturnType<
        typeof createNeglectedOrDelinquentInformationValidationSchema
    >;
}): ReactElement => {
    const { values, errors } =
        useFormikContext<NeglectedOrDelinquentInformationFormValues>();
    const employment = values.employment;
    const canCalculate = PERIOD_COUNT_FIELDS.every(({ key }) =>
        isValidNeglectedOrDelinquentCount(employment[key]));
    const total = canCalculate
        ? PERIOD_COUNT_FIELDS.reduce((sum, { key }) => sum + Number(employment[key]), 0)
        : '';

    return (
        <Panel panelType={PanelType.INFO} heading="Employment">
            <section>
                <p>
                    Provide the <strong>number of students</strong> who obtained
                    employment while in the program:
                </p>
                <div className="nord-employment">
                    {PERIOD_COUNT_FIELDS.map(({ key, label }) => {
                        const field = `employment.${key}`;
                        const inputSchema = object().shape({
                            [field]: reach(validationSchema, field),
                        });
                        const warning = key === 'whileInProgram'
                            ? errors.employmentWhileLimit
                            : errors.employmentExitLimit;
                        return (
                            <div key={key}>
                                <FormTextbox
                                    field={field}
                                    displayName={label}
                                    disabled={disabled}
                                    validationSchema={inputSchema}
                                />
                                {typeof warning === 'string' && (
                                    <p className="nord-demographics-error" role="alert">
                                        {warning}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                    <div className="field-container disabled">
                        <FormInputLabel
                            field="totalStudentsEmployed"
                            displayName="Total Students Employed"
                            disabled
                        />
                        <input
                            id="totalStudentsEmployed"
                            className="data-input"
                            type="text"
                            disabled
                            readOnly
                            value={total}
                        />
                    </div>
                </div>
            </section>
        </Panel>
    );
};

export default NeglectedOrDelinquentEmploymentPanel;
